// @-handle autocomplete, pure: find the `@token` at the caret, filter suggestions, insert the choice.
// Handles look like the API's: lowercase letters, digits and dashes (ElementCreate.handle).

export interface HandleItem {
  handle: string;
  name: string;
  subtype?: string;
  elementId?: string;
  thumbAssetVersionId?: string | null;
  /** the element's current version (for its cover picture) */
  version?: number;
  /** the production the element belongs to; none for the whole workspace */
  projectId?: string | null;
  projectName?: string;
}

export interface MentionQuery {
  /** Index of the `@`. */
  start: number;
  /** Text after the `@` up to the caret (may be empty). */
  query: string;
}

const HANDLE_CHAR = /[a-z0-9-]/i;

/** The `@query` that ends at the caret, when the `@` starts a word. */
export function mentionAt(text: string, caret: number): MentionQuery | null {
  let i = caret;
  while (i > 0 && HANDLE_CHAR.test(text[i - 1]!)) i--;
  if (i === 0 || text[i - 1] !== "@") return null;
  const start = i - 1;
  const before = start === 0 ? "" : text[start - 1]!;
  if (before && !/[\s([{"'“,.;:!?—-]/.test(before)) return null; // e.g. an email address
  return { start, query: text.slice(i, caret) };
}

/** Replaces the open `@query` with `@handle ` and returns the new text and caret. */
export function insertMention(
  text: string,
  caret: number,
  m: MentionQuery,
  handle: string,
): { text: string; caret: number } {
  // Always leave one space after the handle; reuse a space that is already there.
  const after = text.slice(caret).replace(/^ /, "");
  const insert = `@${handle} `;
  return { text: `${text.slice(0, m.start)}${insert}${after}`, caret: m.start + insert.length };
}

/** Prefix matches on the handle first, then matches inside the handle or the name. */
export function filterHandles(items: readonly HandleItem[], query: string, limit = 8): HandleItem[] {
  const q = query.toLowerCase();
  if (!q) return items.slice(0, limit);
  const prefix: HandleItem[] = [];
  const inner: HandleItem[] = [];
  for (const it of items) {
    const h = it.handle.toLowerCase();
    if (h.startsWith(q)) prefix.push(it);
    else if (h.includes(q) || it.name.toLowerCase().includes(q)) inner.push(it);
  }
  return [...prefix, ...inner].slice(0, limit);
}

/** Every distinct `@handle` in a text, in order of appearance. */
export function extractHandles(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/(^|[^a-z0-9@])@([a-z0-9][a-z0-9-]*)/gi)) {
    const h = m[2]!.toLowerCase();
    if (!out.includes(h)) out.push(h);
  }
  return out;
}

/**
 * Normalises the answer of `GET /workspaces/{ws}/handles` or of the elements list. Accepts a bare
 * array or `{items: […]}`, and either {handle, name, subtype, element_id|elementId|id}; `current_version`,
 * `project_id` and `project_name` (a hit from another production) come along when the server sends them.
 */
export function normalizeHandles(raw: unknown): HandleItem[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : [];
  const out: HandleItem[] = [];
  for (const r of list) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    if (typeof o.handle !== "string" || !o.handle) continue;
    const id = o.element_id ?? o.elementId ?? o.id;
    out.push({
      handle: o.handle.replace(/^@/, ""),
      name: typeof o.name === "string" ? o.name : o.handle,
      ...(typeof o.subtype === "string" ? { subtype: o.subtype } : {}),
      ...(typeof id === "string" ? { elementId: id } : {}),
      ...(typeof o.thumbnail_asset_version_id === "string"
        ? { thumbAssetVersionId: o.thumbnail_asset_version_id }
        : {}),
      ...(typeof o.current_version === "number" ? { version: o.current_version } : {}),
      ...(typeof o.project_id === "string" ? { projectId: o.project_id } : {}),
      ...(typeof o.project_name === "string" && o.project_name ? { projectName: o.project_name } : {}),
    });
  }
  return out;
}
